/**
 * How a device pin looks, kept apart from what draws it.
 *
 * A pin says two things at once, because an operator needs both and they come
 * from different places: its colour is the monitor's verdict on the device (on
 * route, strayed, signal gone) and its opacity is how old the fix is. A bright
 * red pin is a device off route now; a faded red one is where a device was off
 * route an hour ago, which is a different problem.
 *
 * These live in their own module so they can be tested. Importing the marker
 * component pulls in react-map-gl, and mapbox-gl does not load under jsdom.
 */
import { MissionDeviceTrack } from '../useMissionDeviceTracks';
import { formatMeters } from '../format';

export const DEVICE_GOLD = '#f5b942';
export const DEVICE_RED = '#ef5350';
export const DEVICE_GREEN = '#66bb6a';
export const DEVICE_MUTED = '#8f9bb3';
/** Fixed collection sites. Not green or red: the route says nothing about them. */
export const DEVICE_FIXED = '#4dd0e1';

/** A fix this new is worth drawing attention to; beyond it the pulse stops. */
export const FRESH_MINUTES = 2;
/** Below this there is no meaningful heading to point, only GPS noise. */
export const MOVING_MPS = 0.7;

export function trackColor(track: MissionDeviceTrack): string {
  // Checked first: a device with no signal has no current position at all, so
  // whatever the last one said about the route is no longer a claim about now.
  if (track.signalState === 'lost') return DEVICE_GOLD;
  // A fixed site is never on or off the route; only its signal is a verdict.
  if (track.isStationary) return DEVICE_FIXED;
  if (track.routeState === 'off_route') return DEVICE_RED;
  if (track.routeState === 'on_route') return DEVICE_GREEN;
  return DEVICE_MUTED;
}

/**
 * Age as transparency, on COP's tiers so the two maps read the same way.
 *
 * Fading rather than hiding is the point. A position from this morning is still
 * the best answer anyone has to where a device is, and removing the pin would
 * replace a weak answer with no answer.
 */
export function trackOpacity(ageMinutes: number): number {
  if (ageMinutes <= 30) return 1;
  if (ageMinutes <= 1440) return 0.7;
  // Also where an unmeasurable age lands, since every comparison against NaN or
  // Infinity is false. That is the right end to fail towards: a fix of unknown
  // vintage is the weakest claim on the map, not the strongest.
  return 0.4;
}

/**
 * The device's name without the kind the roster tacked onto it.
 *
 * Assignees expanded from an entity are labelled `<entity> · <kind>` by the
 * server, so anywhere the kind is already shown the label repeats it. Devices
 * added by id carry no such suffix and come back untouched.
 */
export function trackName(track: MissionDeviceTrack): string {
  const suffix = ` · ${track.kind}`;
  return track.label.endsWith(suffix) ? track.label.slice(0, -suffix.length) : track.label;
}

export function trackStateText(track: MissionDeviceTrack): string {
  if (track.signalState === 'lost') return 'signal lost';
  // Never "off route": a fixed site has no relationship to the line.
  if (track.isStationary) return 'fixed site';
  if (track.routeState === 'off_route') {
    const off = formatMeters(track.offRouteM);
    return off ? `off route by ${off}` : 'off route';
  }
  if (track.routeState === 'on_route') return 'on route';
  // The stream put it on the map; nothing has yet judged it against the line.
  return 'position only';
}

/** Whether to point an arrow, which needs both a bearing and actual movement. */
export function trackIsMoving(track: MissionDeviceTrack): boolean {
  // GPS jitter gives a parked tripod a heading; it has nowhere to point it.
  if (track.isStationary) return false;
  return track.heading != null && (track.speedMps == null || track.speedMps >= MOVING_MPS);
}

export function trackIsFresh(track: MissionDeviceTrack): boolean {
  return track.ageMinutes <= FRESH_MINUTES && track.signalState !== 'lost';
}
